import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { RecipeFormData } from '../components/recipes/RecipeForm';
import { Recipe } from '../types/types';

const csrfToken = () =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

export const fetchRecipes = createAsyncThunk(
  'recipes/fetchRecipes',
  async (scope: 'all' | 'mine' | 'saved' = 'all') => {
    const url = scope === 'all' ? '/api/recipes' : `/api/recipes?scope=${scope}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch recipes');
    return response.json();
  }
);

export const fetchRecipe = createAsyncThunk(
  'recipes/fetchRecipe',
  async (id: number) => {
    const response = await fetch(`/api/recipes/${id}`);
    if (!response.ok) throw new Error('Recipe not found or access denied');
    return response.json() as Promise<Recipe>;
  }
);

export const createRecipe = createAsyncThunk(
  'recipes/create',
  async ({ data, csrfToken }: { data: RecipeFormData; csrfToken: string }) => {
    const response = await fetch('/api/recipes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors || 'Unknown error');
    }
    return response.json();
  }
);

export const updateRecipe = createAsyncThunk(
  'recipes/update',
  async ({ id, data, csrfToken }: { id: number; data: RecipeFormData; csrfToken: string }) => {
    const response = await fetch(`/api/recipes/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'An error occurred');
    }
    return response.json();
  }
);

export const importRecipeFromUrl = createAsyncThunk<RecipeFormData, { url: string; csrfToken: string }>(
  'recipes/importFromUrl',
  async ({ url, csrfToken }) => {
    const response = await fetch('/api/recipes/import_url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ url }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to import recipe');
    }
    return response.json();
  }
);

export const deleteRecipe = createAsyncThunk(
  'recipes/delete',
  async ({ id, csrfToken }: { id: number; csrfToken: string }) => {
    const response = await fetch(`/api/recipes/${id}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'An error occurred');
    }
    return response.json();
  }
);

export const saveRecipe = createAsyncThunk(
  'recipes/save',
  async (id: number) => {
    const response = await fetch(`/api/recipes/${id}/save`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
    });
    if (!response.ok) throw new Error('Failed to save recipe');
    return { id };
  }
);

export const unsaveRecipe = createAsyncThunk(
  'recipes/unsave',
  async (id: number) => {
    const response = await fetch(`/api/recipes/${id}/save`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken() },
    });
    if (!response.ok) throw new Error('Failed to unsave recipe');
    return { id };
  }
);

export const shareRecipe = createAsyncThunk(
  'recipes/share',
  async ({ recipeId, email }: { recipeId: number; email: string }) => {
    const response = await fetch(`/api/recipes/${recipeId}/shares`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to share recipe');
    }
    return { recipeId, share: await response.json() };
  }
);

export const removeShare = createAsyncThunk(
  'recipes/removeShare',
  async ({ recipeId, shareId }: { recipeId: number; shareId: number }) => {
    const response = await fetch(`/api/recipes/${recipeId}/shares/${shareId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken() },
    });
    if (!response.ok) throw new Error('Failed to remove share');
    return { recipeId, shareId };
  }
);

interface RecipesState {
  items: Recipe[];
  currentRecipe: Recipe | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  currentStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: RecipesState = {
  items: [],
  currentRecipe: null,
  status: 'idle',
  currentStatus: 'idle',
  error: null,
}

const recipesSlice = createSlice({
  name: 'recipes',
  initialState,
  reducers: {
    recipeUpdated: (state, action) => {
      const index = state.items.findIndex(recipe => recipe.id === action.payload.id)
      if (index !== -1) state.items[index] = action.payload;
      if (state.currentRecipe?.id === action.payload.id) state.currentRecipe = action.payload;
    },
    clearCurrentRecipe: (state) => {
      state.currentRecipe = null;
      state.currentStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecipes.pending, (state) => { state.status = 'loading'; state.items = []; })
      .addCase(fetchRecipes.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.recipes;
      })
      .addCase(fetchRecipes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(fetchRecipe.pending, (state) => { state.currentStatus = 'loading'; })
      .addCase(fetchRecipe.fulfilled, (state, action) => {
        state.currentStatus = 'succeeded';
        state.currentRecipe = action.payload;
      })
      .addCase(fetchRecipe.rejected, (state, action) => {
        state.currentStatus = 'failed';
        state.error = action.error.message || null;
      })
      .addCase(createRecipe.fulfilled, (state, action) => {
        state.items.push(action.payload.recipe);
      })
      .addCase(updateRecipe.fulfilled, (state, action) => {
        const index = state.items.findIndex(r => r.id === action.payload.recipe.id);
        if (index !== -1) state.items[index] = action.payload.recipe;
        if (state.currentRecipe?.id === action.payload.recipe.id) {
          state.currentRecipe = action.payload.recipe;
        }
      })
      .addCase(deleteRecipe.fulfilled, (state, action) => {
        state.items = state.items.filter(r => r.id !== action.payload.recipe.id);
        if (state.currentRecipe?.id === action.payload.recipe.id) state.currentRecipe = null;
      })
      .addCase(saveRecipe.fulfilled, (state, action) => {
        const item = state.items.find(r => r.id === action.payload.id);
        if (item) item.is_saved = true;
        if (state.currentRecipe?.id === action.payload.id) state.currentRecipe.is_saved = true;
      })
      .addCase(unsaveRecipe.fulfilled, (state, action) => {
        // Remove from list if viewing Saved scope; otherwise just toggle flag
        const item = state.items.find(r => r.id === action.payload.id);
        if (item?.is_saved) {
          state.items = state.items.filter(r => r.id !== action.payload.id);
        } else if (item) {
          item.is_saved = false;
        }
        if (state.currentRecipe?.id === action.payload.id) state.currentRecipe.is_saved = false;
      })
      .addCase(shareRecipe.fulfilled, (state, action) => {
        const { recipeId, share } = action.payload;
        if (state.currentRecipe?.id === recipeId) {
          state.currentRecipe.shares = [...(state.currentRecipe.shares || []), share];
        }
      })
      .addCase(removeShare.fulfilled, (state, action) => {
        const { recipeId, shareId } = action.payload;
        if (state.currentRecipe?.id === recipeId && state.currentRecipe.shares) {
          state.currentRecipe.shares = state.currentRecipe.shares.filter(s => s.id !== shareId);
        }
      });
  },
})

export const { recipeUpdated, clearCurrentRecipe } = recipesSlice.actions
export default recipesSlice.reducer
